/**
 * Bifrost privacy notice and support page. These are the URLs the Claude and
 * ChatGPT directory listings point at, so every statement here must match how
 * Bifrost is built (Odingard/Bifrost: Build Plan §2.4, ADR-0041, ADR-0070).
 */

export type LegalSection = { heading: string; body: string[] };

export type LegalPageDef = {
  path: string;
  title: string;
  lede: string;
  updated: string;
  sections: LegalSection[];
};

export const BIFROST_SUPPORT_EMAIL = "support@odingard.com";
export const BIFROST_PRIVACY_EMAIL = "contact@odingard.com";

export const BIFROST_PRIVACY: LegalPageDef = {
  path: "/bifrost/privacy",
  title: "Bifrost Privacy Notice",
  lede: "How Odingard Security collects, uses and protects information in Bifrost, including the Bifrost MCP connector used by AI assistants.",
  updated: "October 2026",
  sections: [
    {
      heading: "Who we are",
      body: [
        "Bifrost is a product of Odingard Security. Odingard Security operates the hosted Bifrost service and is the controller of account information described below. For evidence and other content a company publishes through Bifrost, that company is the controller and Odingard Security processes it on the company's instructions.",
      ],
    },
    {
      heading: "Information we process",
      body: [
        "Account information: name, work email address and role for people who administer a company's Bifrost workspace, and sign-in records for one-time codes, passkeys and enterprise single sign-on.",
        "Reviewer information: the work email address a buyer's reviewer verifies, agreement (NDA) acceptance records, and access requests and grants.",
        "Customer content: documents, evidence and settings a company uploads, along with the questions asked of it, the answers Bifrost returns, their source citations and their signed receipts.",
        "Field captures: when a company uses verified physical work, the photos, sensor readings and device-reported time and location that its enrolled devices sign.",
        "Security records: an append-only audit log of access, answers and administrative actions.",
      ],
    },
    {
      heading: "The Bifrost MCP connector",
      body: [
        "The shared Bifrost MCP endpoint lets an AI assistant find companies that have chosen to list their trust center, read their public trust profile and ask questions answered only from their public evidence. Companies are not listed unless they turn listing on.",
        "We do not store the IP address or identity of the person using the assistant. A question asked through the connector is recorded in the listed company's audit log, the same way a question asked on its public trust page is.",
        "When an assistant requests a company that is not on Bifrost, we record only the company's domain, the name supplied and how many times it was requested. Bifrost does not email anyone as a result of a connector request.",
      ],
    },
    {
      heading: "How we use information",
      body: [
        "We use information to operate Bifrost: to authenticate users and agents, enforce each company's disclosure policy, answer questions from cited evidence, issue and verify receipts, keep the audit log, provide support and secure the service. We do not sell personal information, and we do not use customer content to train AI models.",
      ],
    },
    {
      heading: "Service providers",
      body: [
        "Google Cloud hosts the service in the United States and runs the AI models through Vertex AI. Resend delivers sign-in and notification email, and Stripe processes payments. Companies that run Bifrost in their own isolated deployment keep their content and models inside their own environment.",
      ],
    },
    {
      heading: "Security",
      body: [
        "Each company's data is isolated with row-level security and its own encryption keys, and customer-managed keys are supported. Agent credentials are bound to the sender (DPoP), restricted material requires approval, and every answer and approved action carries a signed receipt.",
      ],
    },
    {
      heading: "Retention",
      body: [
        "Documents are kept until the company deletes them. Superseded versions are kept for 12 months, and deleted documents are purged after 30 days. Reviewer personal information is kept for 24 months after last activity. Answers, receipts and audit events are kept for 7 years for their evidentiary value. When a company deletes its workspace, its data is purged within 30 days and its keys are destroyed.",
      ],
    },
    {
      heading: "Your choices and rights",
      body: [
        `You can ask us to access, correct or delete your personal information by emailing ${BIFROST_PRIVACY_EMAIL}. If your information is in a company's workspace, we will work with that company to respond. Erasure replaces your personal information in audit records with a pseudonymous identifier so the record stays verifiable.`,
      ],
    },
    {
      heading: "Contact",
      body: [`Odingard Security, ${BIFROST_PRIVACY_EMAIL}.`],
    },
  ],
};

export const BIFROST_SUPPORT: LegalPageDef = {
  path: "/bifrost/support",
  title: "Bifrost Support",
  lede: "Help with Bifrost workspaces, trust pages, receipts and the Bifrost MCP connector.",
  updated: "October 2026",
  sections: [
    {
      heading: "Contact support",
      body: [
        `Email ${BIFROST_SUPPORT_EMAIL} with your company name, the workspace or trust page involved and, for an answer or action, its receipt ID.`,
      ],
    },
    {
      heading: "Using Bifrost from an AI assistant",
      body: [
        "Add the Bifrost connector in Claude or ChatGPT, then ask about a company, for example \"Is Acme on Bifrost, and do they encrypt data at rest?\". The connector finds the company's trust center if it is listed, answers from its public evidence with citations and a receipt, or records a request for the company to join.",
        "For NDA or restricted evidence, connect to the company's own Bifrost endpoint, shown in the connector's results, and sign in through the company's approval flow.",
      ],
    },
    {
      heading: "Checking a receipt",
      body: [
        "Every answer has a receipt ID. Check it from the verify link in the answer, or offline with the open-source @odingard/bifrost-verify package.",
      ],
    },
    {
      heading: "Listing your trust center",
      body: [
        "Workspace owners and admins can turn on \"List my trust center in Bifrost's public search\" from the trust-domain page in the Bifrost console. Verify your domain on the same page so assistants show your company as domain-verified.",
      ],
    },
    {
      heading: "Security issues",
      body: [
        "Report vulnerabilities through our responsible disclosure process at /security-disclosure.",
      ],
    },
  ],
};

export const LEGAL_PAGES = [BIFROST_PRIVACY, BIFROST_SUPPORT];
export const legalPage = (path: string) =>
  LEGAL_PAGES.find((p) => p.path === path);
